import { useRef, useEffect, useMemo } from 'react'
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme, alpha } from '@mui/material/styles'
import { chronological, fmtSeconds } from './vocabulary'

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer])

// One percentile across several series, drawn as lines over the day axis.
// Two callers: the hop decomposition (which hop moved — end→durable rising
// with end→declared is dormant clients, rising with declared→durable is the
// upload lane) and the same lag split by seal cause (which cause carries the
// tail). Series are a prop rather than a constant because the cause list is
// discovered from the window, not known here.
//
// Log scale by default because the healthy range and the tail are orders
// apart (a seconds-long p50 against an hours-long max) and a linear axis
// flattens the p50 onto the axis. It falls back to linear on its own when a value is
// non-positive: `end_to_declared` goes negative on a device whose clock
// runs ahead of the server's, and a log axis silently drops those points —
// hiding exactly the anomaly worth seeing.
const LagChart = ({
  days,
  series,
  percentile,
  height = 260,
  sampleOf,
  highlighted,
  // Latency is the default reading, but the capture section draws ratios on
  // this same chart: `format` renders the axis and the tooltip, and `scale`
  // overrides the log/linear choice below — coverage lives in [0, 1] and a log
  // axis there magnifies the healthy end and flattens the collapse.
  format = fmtSeconds,
  scale,
  sampleNoun = 'spans sealed',
}) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  // Held in a ref like Heartbeats' onZoom: callers pass an inline arrow, so
  // depending on it would re-enter the whole option on every render.
  const sampleOfRef = useRef(sampleOf)
  sampleOfRef.current = sampleOf
  const theme = useTheme()

  const rows = useMemo(() => chronological(days), [days])

  const nonPositive = useMemo(
    () =>
      rows.some((row) =>
        series.some(({ valueOf }) => {
          const value = valueOf(row, percentile)
          return value != null && value <= 0
        })
      ),
    [rows, series, percentile]
  )

  // As in StackedDayChart: the empty state attaches no ref, so there is
  // nothing to initialise until a day arrives.
  const isEmpty = !days.length

  useEffect(() => {
    if (isEmpty || !containerRef.current) return undefined
    const chart = echarts.init(containerRef.current)
    chartRef.current = chart
    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [isEmpty])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    const textColor = theme.palette.text.secondary
    const gridColor = alpha(textColor, 0.15)

    const tooltipFormatter = (params) => {
      const lines = params
        .map(
          ({ marker, seriesName, value }) =>
            `<div style="display:flex;justify-content:space-between;gap:18px;">
              <span>${marker}${seriesName}</span><b>${format(value)}</b>
            </div>`
        )
        .join('')
      const row = rows.find(({ day }) => day === params[0].axisValue)
      const sample =
        row && sampleOfRef.current ? sampleOfRef.current(row) : null
      return `<div style="min-width:200px">
        <div style="margin-bottom:4px;">${params[0].axisValueLabel} UTC</div>
        ${lines}
        ${
          sample != null
            ? `<div style="opacity:.7;margin-top:4px;">${sample} ${sampleNoun}</div>`
            : ''
        }
      </div>`
    }

    chart.setOption(
      {
        animationDuration: 500,
        animationDurationUpdate: 300,
        grid: { left: 56, right: 16, top: 12, bottom: 24 },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' },
          backgroundColor: 'rgba(33, 33, 41, 0.94)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: tooltipFormatter,
        },
        xAxis: {
          type: 'category',
          data: rows.map(({ day }) => day),
          axisLabel: {
            color: textColor,
            fontSize: 10,
            formatter: (day) => day.slice(5),
          },
          axisLine: { lineStyle: { color: gridColor } },
          axisTick: { show: false },
        },
        yAxis: {
          type: scale || (nonPositive ? 'value' : 'log'),
          axisLabel: {
            color: textColor,
            fontSize: 10,
            formatter: (value) => format(value),
          },
          splitLine: { lineStyle: { color: gridColor } },
        },
        series: series.map(({ key, label, color, valueOf }) => ({
          name: label,
          type: 'line',
          // `connectNulls: false` on purpose: a day with no sample is a gap
          // in the line, never a segment drawn across it. Durable→processed is
          // empty until step 2 lands, and a cause simply may not occur on a day.
          connectNulls: false,
          data: rows.map((row) => valueOf(row, percentile)),
          color,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: { width: 1.5 },
          emphasis: { focus: 'series' },
        })),
      },
      { replaceMerge: ['series'] }
    )
  }, [rows, series, percentile, nonPositive, theme, format, scale, sampleNoun])

  // Hovering the page's legend emphasises that series and blurs the rest —
  // both charts set `emphasis: { focus: 'series' }`, so ECharts does the
  // blurring; this only has to say which one. Declared after the option
  // effect so a fresh setOption never lands on top of a live highlight.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return undefined
    const entry = highlighted
      ? series.find((candidate) => candidate.key === highlighted)
      : null
    if (!entry) {
      chart.dispatchAction({ type: 'downplay' })
      return undefined
    }
    chart.dispatchAction({ type: 'highlight', seriesName: entry.label })
    return () =>
      chart.dispatchAction({ type: 'downplay', seriesName: entry.label })
  }, [highlighted, series])

  return (
    <Box sx={{ width: '100%' }}>
      {/* The chart's own box, sized so the empty-state overlay can fill it.
          The caption below sits outside it, or it would overflow. */}
      <Box sx={{ position: 'relative', width: '100%', height }}>
        {/* ECharts owns this node's children outright — it fills them on
            init and clears them on dispose. React must therefore never
            render anything into it, and never reuse it for anything else:
            the empty state is a *sibling* rather than the other branch of a
            ternary, because a ternary lets React reconcile the two as the
            same div, hand it to ECharts, and then fail to remove a child
            ECharts had already cleared away (the crash of 2026-09-14). */}
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height,
            visibility: isEmpty ? 'hidden' : 'visible',
          }}
        />
        {isEmpty && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No spans sealed in this window.
            </Typography>
          </Box>
        )}
      </Box>
      {nonPositive && !isEmpty && (
        <Typography variant="caption" color="warning.main">
          Linear scale: a lag is zero or negative in this window, which a log
          axis would drop. A negative end → declared is a device clock ahead of
          the server's.
        </Typography>
      )}
    </Box>
  )
}

export default LagChart
